import { Info, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { AuthService } from '@/services/auth.service'
import { useAuth } from '@/auth/Auth'
import { isOnboardingStepFinished } from '@/hooks/useSecurityChallenge'
import { SecurityChallengeModal } from '@/components/shared/SecurityChallenge'

import OnboardingSheet from '@/components/Onboarding/OnboardingSheet'
import CustomInputMask from '@/components/shared/CustomInputMask/CustomInputMask'
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect'
import { Input } from '@/components/shared/Input/Input'
import { PhoneInput } from '@/components/shared/PhoneInput/PhoneInput'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { SheetFooter, SheetTitle } from '@/components/ui/sheet'

import { cnpjMask, cpfMask } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'
import { useOnboardingBankStep } from '../hooks/useOnboardingBankStep'

import { ComboBox } from '@/components/shared/ComboBox/ComboBox'
import { GetBanksService } from '@/services/banks.service'
import InternationalAccount from './international-account'
import { Notice } from '@/components/shared/Notice'
import { CircleFlag } from 'react-circle-flags'

interface BankStepProps {
  open: boolean
  onClose: () => void
  isPending: boolean
  isEditing?: boolean
  isOnboarding?: boolean
  initialCurrency?: 'BRL' | 'USD'
  canEdit?: boolean
}

function InputFieldError({ message }: { message?: string }) {
  return <p className="text-sm text-error mt-1 min-h-5">{message}</p>
}

export function BankStep({
  open,
  onClose,
  isPending,
  isEditing,
  isOnboarding = false,
  initialCurrency,
  canEdit = true,
}: BankStepProps) {
  const { t } = useTranslation(['onboarding', 'withdrawal'])
  const { authData } = useAuth()
  const [isChallengeOpen, setIsChallengeOpen] = useState(false)
  const [pendingBankData, setPendingBankData] = useState<any>(null)
  const pendingBankDataRef = useRef<any>(null)
  const {
    onSubmit,
    handleSubmit,
    isSubmitting,
    register,
    isLoading,
    isDirty,
    setValue,
    defaultValues,
    errors,
    clearErrors,
    watch,
  } = useOnboardingBankStep({
    onClose,
    mode: isEditing ? 'edit' : 'create',
    isOnboarding,
    onChallengeTokenError: () => setIsChallengeOpen(true),
  })

  const { data: userInfo } = useQuery({
    queryKey: ['user-info'],
    queryFn: async () => await AuthService.userInfo(),
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const isMultiWalletEnabled = userInfo?.multi_wallet_enabled ?? false
  const isOnboardingFinished =
    isOnboardingStepFinished(userInfo?.onboarding_step) ||
    isOnboardingStepFinished(authData?.onboarding_step)

  const needsChallenge = !isOnboarding && isOnboardingFinished

  useEffect(() => {
    if (open) return
    setIsChallengeOpen(false)
    setPendingBankData(null)
    pendingBankDataRef.current = null
  }, [open])

  const handleGatedSubmit = (data: any) => {
    if (!canEdit) return
    pendingBankDataRef.current = data
    setPendingBankData(data)
    if (needsChallenge) {
      setIsChallengeOpen(true)
      return
    }
    onSubmit(data)
  }

  const handleInvalidSubmit = (formErrors: Record<string, { message?: string }>) => {
    const firstError = Object.values(formErrors ?? {})[0]
    if (firstError?.message) {
      toast({ description: String(firstError.message), variant: 'destructive' })
    }
  }

  const currency = watch('currency') || defaultValues?.currency || 'BRL'

  useEffect(() => {
    if (!open) return
    if (!initialCurrency) return

    setValue('currency', initialCurrency, { shouldDirty: false })
  }, [initialCurrency, open, setValue])

  const pixKeyType = watch('pix_key_type')
  const previousPixKeyType = useRef(pixKeyType)

  useEffect(() => {
    if (
      pixKeyType &&
      pixKeyType !== previousPixKeyType.current &&
      previousPixKeyType.current
    ) {
      setValue('pix_key', '')
      setValue('phone_prefix', '')
    }
    previousPixKeyType.current = pixKeyType
  }, [pixKeyType, setValue])

  const keyTypeOptions = [
    { value: 'individual', label: t('common:keyType.cpf') },
    { value: 'business', label: t('common:keyType.cnpj') },
    { value: 'email', label: t('common:keyType.email') },
    { value: 'phone', label: t('common:keyType.phone') },
    { value: 'evp', label: t('common:keyType.random') },
  ]

  const accountTypeOptions = [
    { value: 'operational', label: t('common:accountType.operational') },
    { value: 'reserve', label: t('common:accountType.reserve') },
  ]

  const handleAgencyDigitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase()
    if (/^[0-9X]?$/.test(value)) {
      setValue('agency_digit', value, { shouldDirty: true })
      if (value) {
        clearErrors('agency_digit')
      }
    }
  }

  const renderPixInput = (type: string) => {
    const currentPixKey = watch('pix_key')

    switch (type) {
      case 'individual':
        return (
          <CustomInputMask
            mask={cpfMask}
            value={cpfMask(currentPixKey || '')}
            onChange={(e) => {
              const onlyNumbers = e.target.value.replace(/\D/g, '').slice(0, 11)
              setValue('pix_key', onlyNumbers, { shouldDirty: true })
            }}
            label={t('key_cpf')}
            placeholder={t('cpf_placeholder')}
            helperText={errors.pix_key?.message}
            disabled={!canEdit}
          />
        )
      case 'business':
        return (
          <CustomInputMask
            mask={cnpjMask}
            value={cnpjMask(currentPixKey || '')}
            onChange={(e) => {
              const onlyNumbers = e.target.value.replace(/\D/g, '').slice(0, 14)
              setValue('pix_key', onlyNumbers, { shouldDirty: true })
            }}
            label={t('key_cnpj')}
            placeholder={t('cnpj_placeholder')}
            helperText={errors.pix_key?.message}
            disabled={!canEdit}
          />
        )
      case 'email':
        return (
          <Input
            {...register('pix_key')}
            label={t('key_email')}
            placeholder={t('email_placeholder')}
            type="email"
            value={currentPixKey || ''}
            onChange={(e) => setValue('pix_key', e.target.value, { shouldDirty: true })}
            helperText={errors.pix_key?.message}
            disabled={!canEdit}
          />
        )
      case 'phone':
        return (
          <PhoneInput
            label={t('key_phone')}
            phone_prefix={watch('phone_prefix') || defaultValues?.phone_prefix || '55'}
            defaultValue={currentPixKey || ''}
            onChangePhone={(value, prefix) => {
              setValue('pix_key', String(value || '').replace(/\D/g, ''), { shouldDirty: true })
              setValue('phone_prefix', String(prefix || '').replace(/\D/g, ''), { shouldDirty: true })
            }}
            helperText={errors.pix_key?.message}
            disabled={!canEdit}
          />
        )
      case 'evp':
        return (
          <Input
            {...register('pix_key')}
            label={t('key_random')}
            placeholder={t('key_placeholder')}
            value={currentPixKey || ''}
            onChange={(e) => setValue('pix_key', e.target.value, { shouldDirty: true })}
            helperText={errors.pix_key?.message}
            disabled={!canEdit}
          />
        )
      default:
        return null
    }
  }

  async function fetchBankOptions(query?: string) {
    return await GetBanksService.getBanks(query)
  }

  const currentPixKeyType = pixKeyType || defaultValues?.pix_key_type

  return (
    <>
      <OnboardingSheet
        open={open}
        onClose={onClose}
        isPending={isPending}
        hasHeader={!isEditing}
      >
        <form
          onSubmit={handleSubmit(handleGatedSubmit, handleInvalidSubmit)}
          className={`flex flex-col justify-between ${isEditing ? 'h-full' : 'h-[calc(100vh-140px)]'
            }`}
        >
        <section className="p-6">
          <SheetTitle>{t('bank_account')}</SheetTitle>
          <section className="py-8 space-y-4 border-b border-b-colorBorder">
            <SheetTitle>{t('currency_info')}</SheetTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { value: 'BRL', labelKey: 'currency_brl', code: 'br' },
                { value: 'USD', labelKey: 'currency_usd', code: 'us' },
              ].map((item) => {
                const isSelected = currency === item.value
                return (
                  <button
                    key={item.value}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => {
                      if (!canEdit) return
                      setValue('currency', item.value as 'BRL' | 'USD')
                    }}
                    className={`
                      flex items-center gap-2.5 px-4 py-3 rounded-xl border transition-all duration-200
                      ${!canEdit ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                      ${isSelected
                        ? 'border-lime-500/60 bg-lime-500/10 ring-1 ring-lime-500/20'
                        : 'border-zinc-700/60 bg-zinc-800/50 hover:border-zinc-600 hover:bg-zinc-800'
                      }
                    `}
                  >
                    <span className="w-6 h-6 shrink-0 overflow-hidden rounded-full">
                      <CircleFlag countryCode={item.code} height={24} />
                    </span>
                    <div className="flex flex-col items-start">
                      <span className={`text-sm font-semibold leading-tight ${isSelected ? 'text-white' : 'text-zinc-400'}`}>
                        {item.value}
                      </span>
                      <span className={`text-[11px] leading-tight ${isSelected ? 'text-zinc-300' : 'text-zinc-500'}`}>
                        {t(item.labelKey)}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
            {isMultiWalletEnabled && (
              <Notice variant={'warning'} message={t('withdrawal_info')} />
            )}{' '}
          </section>
          {currency === 'BRL' && (
            <section className="mt-6 space-y-4">
              <fieldset disabled={!canEdit} className="space-y-4 border-0 p-0 m-0 min-w-0">
              <ComboBox
                label={t('bank_account')}
                fetchOptions={fetchBankOptions}
                value={defaultValues?.bank_code}
                onChange={(value) => setValue('bank_code', value)}
                placeholder={t('select_bank')}
                helperText={errors.bank_code?.message}
                disabled={!canEdit}
              />

              <div className="grid grid-cols-2 flex-col gap-4 md:flex-row">
                <div className="col-span-2">
                  <CustomSelect
                    label={t('account_type')}
                    placeholder={t('select_account_type')}
                    options={accountTypeOptions}
                    helperText={errors.account_type?.message}
                    hasError={!!errors.account_type}
                    onValueChange={(e) => {
                      setValue('account_type', e)
                      clearErrors('account_type')
                    }}
                    value={defaultValues?.account_type}
                    className="col-span-2"
                    disabled={!canEdit}
                  />
                </div>

                <div className="col-span-2">
                  <Alert className="border-yellow-500 w-full">
                    <Info className="h-4 w-4" color="#eab308" />
                    <AlertDescription className="text-yellow-500">
                      {t('agency_digit_help')}
                    </AlertDescription>
                  </Alert>
                </div>

                <div className="col-span-2 grid grid-cols-2 gap-4 w-full">
                  <div>
                    <Input
                      {...register('agency_number')}
                      label={t('agency')}
                      placeholder={t('0000')}
                      type="number"
                      hasError={!!errors.agency_number}
                      disabled={!canEdit}
                    />
                    <InputFieldError message={errors.agency_number?.message} />
                  </div>
                  <div>
                    <Input
                      label={t('agency_digit')}
                      placeholder={t('0')}
                      value={watch('agency_digit') || ''}
                      onChange={handleAgencyDigitChange}
                      hasError={!!errors.agency_digit}
                      maxLength={1}
                      disabled={!canEdit}
                    />
                    <InputFieldError message={errors.agency_digit?.message} />
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-4 pb-6">
                <div className="flex-1">
                  <Input
                    label={t('account')}
                    type="number"
                    placeholder={t('0000000000')}
                    {...register('account_number')}
                    hasError={!!errors.account_number}
                    disabled={!canEdit}
                  />
                  <InputFieldError message={errors.account_number?.message} />
                </div>
                <div className="flex-1">
                  <Input
                    {...register('account_digit')}
                    label={t('account_digit')}
                    placeholder={t('00')}
                    hasError={!!errors.account_digit}
                    maxLength={1}
                    disabled={!canEdit}
                  />
                  <InputFieldError message={errors.account_digit?.message} />
                </div>
              </div>
              <section className="py-8 border-t border-t-colorBorder">
                <SheetTitle>{t('Pix')}</SheetTitle>
                <div
                  className={`grid gap-4 mt-4 ${
                    currentPixKeyType === 'phone'
                      ? 'grid-cols-1'
                      : 'grid-cols-2 max-sm:grid-cols-1'
                  }`}
                >
                  <CustomSelect
                    options={keyTypeOptions}
                    label={t('key_type')}
                    placeholder={t('select_key_type')}
                    helperText={errors.pix_key_type?.message}
                    hasError={!!errors.pix_key_type}
                    onValueChange={(e) => setValue('pix_key_type', e)}
                    value={currentPixKeyType}
                    disabled={!canEdit}
                  />
                  {currentPixKeyType && renderPixInput(currentPixKeyType)}
                </div>
              </section>
              </fieldset>
            </section>
          )}

          {currency === 'USD' && (
            <InternationalAccount mode={isEditing ? 'edit' : 'create'} canEdit={canEdit} isOnboarding={isOnboarding} />
          )}
        </section>

        {currency === 'BRL' && (
          <SheetFooter className="flex gap-2 border-t border-t-colorBorder p-4 max-md:pb-24 justify-end">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              disabled={
                !canEdit ||
                isSubmitting ||
                isLoading ||
                (Boolean(isEditing) && !isDirty)
              }
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin" />
                  {t('saving')}
                </span>
              ) : (
                t('save')
              )}
            </Button>
          </SheetFooter>
        )}
        </form>
      </OnboardingSheet>

      <SecurityChallengeModal
        open={isChallengeOpen}
        purpose="bank_account_update"
        email={userInfo?.email ?? authData?.email}
        metadata={{ currency: 'BRL' }}
        onVerified={(token) => {
          const payload = pendingBankDataRef.current ?? pendingBankData
          setIsChallengeOpen(false)
          if (payload && token) {
            onSubmit(payload, token)
          }
        }}
        onCancel={() => setIsChallengeOpen(false)}
      />
    </>
  )
}
